import React, { useState, useEffect } from 'react';

const CommissionsCommercialesComponent = ({ anneeScolaire = '2025/2026', etudiants = [], commerciaux = [] }) => {
  const COMMISSION_NORMALE = 700; // DH pour commerciaux normaux (dynamiques)

  const [commerciauxDynamiques, setCommerciauxDynamiques] = useState([]);
  const [commerciauxStatistiques, setCommerciauxStatistiques] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCommercial, setEditingCommercial] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    nombreEtudiants: 0,
    commissionParEtudiant: 1000
  });

  useEffect(() => {
    calculerCommerciauxDynamiques();
    chargerCommerciauxStatistiques();
  }, [anneeScolaire, etudiants, commerciaux]);

  // Calculer les commerciaux dynamiques (vrais étudiants)
  const calculerCommerciauxDynamiques = () => {
    // Filtrer par année ET exclure les partners
    const etudiantsFiltres = anneeScolaire === 'toutes'
      ? etudiants.filter(e => e.isPartner !== true)
      : etudiants.filter(e => e.anneeScolaire === anneeScolaire && e.isPartner !== true);

    const commissionsParCommercial = {};

    etudiantsFiltres.forEach(etudiant => {
      const commercialId = etudiant.commercial;

      // Ignorer les étudiants sans commercial assigné
      if (!commercialId) return;

      if (!commissionsParCommercial[commercialId]) {
        // ✅ Trouver le nom du commercial depuis la liste des commerciaux
        const commercialInfo = commerciaux.find(c => c._id === commercialId);

        commissionsParCommercial[commercialId] = {
          nom: commercialInfo ? commercialInfo.nom : `Commercial inconnu`,
          nombreEtudiants: 0,
          commissionParEtudiant: COMMISSION_NORMALE,
          type: 'dynamique'
        };
      }

      commissionsParCommercial[commercialId].nombreEtudiants += 1;
    });

    // ✅ Filtrer pour exclure Aicha Achaf
    const liste = Object.values(commissionsParCommercial)
      .filter(c => c.nom.toLowerCase() !== 'aicha achaf')
      .sort((a, b) => b.nombreEtudiants - a.nombreEtudiants);

    setCommerciauxDynamiques(liste);
  };

  // Charger les commerciaux statistiques depuis le backend
  const chargerCommerciauxStatistiques = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('    https://vmi1977988.contaboserver.net/api2/commerciaux-statistiques', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // ✅ Filtrer pour exclure Aicha Achaf
        const commerciauxFiltres = data.filter(c =>
          c.nom.toLowerCase() !== 'aicha achaf'
        );
        setCommerciauxStatistiques(commerciauxFiltres.map(c => ({
          ...c,
          type: 'statistique'
        })));
      }
    } catch (error) {
      console.error('Erreur chargement commerciaux statistiques:', error);
    }
  };

  // Combiner les deux listes
  const tousLesCommerciaux = [
    ...commerciauxDynamiques,
    ...commerciauxStatistiques
  ].sort((a, b) => b.nombreEtudiants - a.nombreEtudiants);

  // Calculer les totaux
  const totalEtudiants = tousLesCommerciaux.reduce((sum, c) => sum + c.nombreEtudiants, 0);
  const commissionTotale = tousLesCommerciaux.reduce((sum, c) =>
    sum + (c.nombreEtudiants * c.commissionParEtudiant), 0
  );

  // Ajouter un commercial statistique
  const handleAjouter = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('    https://vmi1977988.contaboserver.net/api2/commerciaux-statistiques', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Commercial ajouté avec succès');
        setShowModal(false);
        setFormData({ nom: '', nombreEtudiants: 0, commissionParEtudiant: 1000 });
        chargerCommerciauxStatistiques();
      } else {
        const error = await response.json();
        alert(error.message || 'Erreur lors de l\'ajout');
      }
    } catch (error) {
      console.error('Erreur ajout commercial:', error);
      alert('Erreur lors de l\'ajout');
    }
  };

  // Modifier un commercial statistique
  const handleModifier = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`     https://vmi1977988.contaboserver.net/api2/commerciaux-statistiques/${editingCommercial._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Commercial modifié avec succès');
        setShowModal(false);
        setEditingCommercial(null);
        setFormData({ nom: '', nombreEtudiants: 0, commissionParEtudiant: 1000 });
        chargerCommerciauxStatistiques();
      } else {
        alert('Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Erreur modification commercial:', error);
      alert('Erreur lors de la modification');
    }
  };

  // Supprimer un commercial statistique
  const handleSupprimer = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commercial ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`     https://vmi1977988.contaboserver.net/api2/commerciaux-statistiques/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Commercial supprimé avec succès');
        chargerCommerciauxStatistiques();
      }
    } catch (error) {
      console.error('Erreur suppression commercial:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Ouvrir modal pour modifier
  const ouvrirModalModification = (commercial) => {
    setEditingCommercial(commercial);
    setFormData({
      nom: commercial.nom,
      nombreEtudiants: commercial.nombreEtudiants,
      commissionParEtudiant: commercial.commissionParEtudiant
    });
    setShowModal(true);
  };

  // Export Excel
  const exportToExcel = () => {
    let csvContent = '\uFEFF';

    csvContent += 'TABLEAU DES COMMISSIONS COMMERCIALES\n';
    csvContent += `Année scolaire,${anneeScolaire}\n`;
    csvContent += `Date d'export,${new Date().toLocaleDateString('fr-FR')}\n`;
    csvContent += `Total étudiants,${totalEtudiants}\n`;
    csvContent += `Commission totale,${commissionTotale.toLocaleString('fr-FR')} DH\n`;
    csvContent += '\n';

    csvContent += 'CLASSEMENT DES COMMERCIAUX\n';
    csvContent += 'Rang,Commercial,Type,Nb Étudiants,Commission/Étudiant,Commission Totale,Pourcentage\n';
    tousLesCommerciaux.forEach((item, index) => {
      const commissionTotaleCommercial = item.nombreEtudiants * item.commissionParEtudiant;
      const percentage = totalEtudiants > 0 ? ((item.nombreEtudiants / totalEtudiants) * 100).toFixed(1) : 0;
      csvContent += `${index + 1},"${item.nom}",${item.type === 'dynamique' ? 'Dynamique' : 'Statistique'},${item.nombreEtudiants},${item.commissionParEtudiant},${commissionTotaleCommercial.toLocaleString('fr-FR')},${percentage}%\n`;
    });
    csvContent += `TOTAL,,,${totalEtudiants},,${commissionTotale.toLocaleString('fr-FR')},100%\n`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `Commissions_Commerciales_${anneeScolaire.replace('/', '-')}_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ background: '#fff', padding: '1.5rem', fontFamily: 'Arial, sans-serif' }}>
      {/* En-tête */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid #000'
      }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#000', margin: 0 }}>
          Tableau des Commissions Commerciales - {anneeScolaire}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => {
              setEditingCommercial(null);
              setFormData({ nom: '', nombreEtudiants: 0, commissionParEtudiant: 1000 });
              setShowModal(true);
            }}
            style={{
              padding: '0.5rem 1rem',
              background: '#3B82F6',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}
          >
            + Ajouter Commercial Statistique
          </button>
          <button
            onClick={exportToExcel}
            style={{
              padding: '0.5rem 1rem',
              background: '#217346',
              color: '#fff',
              border: '1px solid #1a5c37',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}
          >
            Exporter Excel
          </button>
        </div>
      </div>

      {/* Résumé */}
      <div style={{ marginBottom: '2rem' }}>
        <table style={{
          width: '100%',
          maxWidth: '600px',
          borderCollapse: 'collapse',
          border: '1px solid #000'
        }}>
          <tbody>
            <tr style={{ background: '#f0f0f0' }}>
              <td style={{ padding: '0.5rem', border: '1px solid #000', fontWeight: 'bold', width: '250px' }}>
                Total Étudiants
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                {totalEtudiants}
              </td>
            </tr>
            <tr style={{ background: '#fff' }}>
              <td style={{ padding: '0.5rem', border: '1px solid #000', fontWeight: 'bold' }}>
                Commission Totale
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                {commissionTotale.toLocaleString('fr-FR')} DH
              </td>
            </tr>
            <tr style={{ background: '#f0f0f0' }}>
              <td style={{ padding: '0.5rem', border: '1px solid #000', fontWeight: 'bold' }}>
                Nombre de Commerciaux
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                {tousLesCommerciaux.length}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Tableau principal */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
          Classement des Commerciaux
        </h3>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: '1px solid #000'
        }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold', width: '60px' }}>
                Rang
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'left', fontWeight: 'bold' }}>
                Commercial
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold', width: '100px' }}>
                Type
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold', width: '120px' }}>
                Nb Étudiants
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold', width: '120px' }}>
                Commission/Ét
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold', width: '150px' }}>
                Commission Tot
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold', width: '100px' }}>
                %
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold', width: '150px' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {tousLesCommerciaux.map((item, index) => {
              const commissionTotaleCommercial = item.nombreEtudiants * item.commissionParEtudiant;
              const percentage = totalEtudiants > 0 ? ((item.nombreEtudiants / totalEtudiants) * 100).toFixed(1) : 0;

              return (
                <tr key={`${item.type}-${item._id || item.nom}`} style={{ background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                    {index + 1}
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000' }}>
                    {item.nom}
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      background: item.type === 'dynamique' ? '#e0f2fe' : '#fef3c7',
                      color: item.type === 'dynamique' ? '#0369a1' : '#b45309'
                    }}>
                      {item.type === 'dynamique' ? 'Dynamique' : 'Statistique'}
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                    {item.nombreEtudiants}
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                    {item.commissionParEtudiant} DH
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                    {commissionTotaleCommercial.toLocaleString('fr-FR')} DH
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                    {percentage}%
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                    {item.type === 'statistique' ? (
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => ouvrirModalModification(item)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: '#3B82F6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleSupprimer(item._id)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: '#EF4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          Supprimer
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>-</span>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr style={{ background: '#e0e0e0', fontWeight: 'bold' }}>
              <td colSpan="3" style={{ padding: '0.5rem', border: '1px solid #000' }}>
                TOTAL
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                {totalEtudiants}
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                -
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                {commissionTotale.toLocaleString('fr-FR')} DH
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                100%
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000' }}></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            border: '1px solid #000'
          }}>
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.125rem', fontWeight: 'bold' }}>
              {editingCommercial ? 'Modifier Commercial Statistique' : 'Ajouter Commercial Statistique'}
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Nom du Commercial
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                disabled={!!editingCommercial}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #000',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
                placeholder="Ex: Amina, Khaoula, Meriem..."
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Nombre d'Étudiants
              </label>
              <input
                type="number"
                min="0"
                value={formData.nombreEtudiants}
                onChange={(e) => setFormData({ ...formData, nombreEtudiants: parseInt(e.target.value) || 0 })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #000',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Commission par Étudiant (DH)
              </label>
              <input
                type="number"
                min="0"
                value={formData.commissionParEtudiant}
                onChange={(e) => setFormData({ ...formData, commissionParEtudiant: parseInt(e.target.value) || 0 })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #000',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingCommercial(null);
                  setFormData({ nom: '', nombreEtudiants: 0, commissionParEtudiant: 1000 });
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Annuler
              </button>
              <button
                onClick={editingCommercial ? handleModifier : handleAjouter}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#3B82F6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {editingCommercial ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionsCommercialesComponent;